import http from './http'
import { useStats } from '@/stores/stats'

export const getMetrics = () => {
  const statsStore = useStats()
  const issuesToConsider = statsStore.issuesMetadata
    ? Object.keys(statsStore.issuesMetadata.issueTypes)
    : []

  return http.$get('/dashboard/metrics').then(response => {
    let overviewStats = {}

    if (response.quality?.percent) {
      overviewStats = {
        clean: response.quality.percent.clean,
        dirty: response.quality.percent.dirty,
      }
    }

    const duplicateSetsToReview = []
    const mergedDuplicatesSets = []
    const undoMergeErrors = []
    const mergeErrors = []
    const fixedIssues = []
    const fixErrors = []
    const undoFixErrors = []

    Object.keys(response.dupeset_by_directors).forEach(director => {
      const directorTotalSets =
        response.dupeset_by_directors[director].open.total
      // null (not 0) when the backend does not report record counts yet, so
      // the chart can fall back to set counts instead of showing empty bars
      const directorRecordCount =
        response.dupeset_by_directors[director].open.record_count ?? null
      const directorTotalManual =
        response.dupeset_by_directors[director].written.success.manual
      const directorTotalUndone =
        response.dupeset_by_directors[director].written.undone
      const directorTotalUndoFailed =
        response.dupeset_by_directors[director].written.undo_failed
      const directorTotalAuto =
        response.dupeset_by_directors[director].written.success.auto
      const directorTotalMerged =
        directorTotalManual + directorTotalAuto + directorTotalUndone
      const directorTotalFailed =
        response.dupeset_by_directors[director].written.failed

      if (directorTotalSets) {
        duplicateSetsToReview.push({
          director,
          count: directorTotalSets,
          recordCount: directorRecordCount,
        })
      }

      if (directorTotalMerged) {
        mergedDuplicatesSets.push({
          director,
          countManual: directorTotalManual,
          countAuto: directorTotalAuto,
          countUndone: directorTotalUndone,
        })
      }

      if (directorTotalUndoFailed) {
        undoMergeErrors.push({
          director,
          count: directorTotalUndoFailed,
        })
      }

      if (directorTotalFailed) {
        mergeErrors.push({
          director,
          count: directorTotalFailed,
        })
      }
    })

    const queuedDuplicateSets = []
    Object.keys(response.dupeset_by_directors).forEach(director => {
      const directorQueuedManual =
        response.dupeset_by_directors[director].queued?.manual || 0
      const directorQueuedAuto =
        response.dupeset_by_directors[director].queued?.auto || 0
      const directorQueuedUndo =
        response.dupeset_by_directors[director].queued?.undo || 0
      const directorTotalQueued =
        directorQueuedManual + directorQueuedAuto + directorQueuedUndo

      if (directorTotalQueued) {
        queuedDuplicateSets.push({
          director,
          countManual: directorQueuedManual,
          countAuto: directorQueuedAuto,
          countUndo: directorQueuedUndo,
        })
      }
    })

    // Orgs without the Data Issues license get no recordissue payload at all,
    // so every branch below tolerates the block, and any key inside it
    const recordissue = response.recordissue ?? {}

    // by_rule → dataToUpdateByRule
    const dataToUpdateByRule = []
    Object.keys(recordissue.by_rule ?? {}).forEach(directorId => {
      const rule = recordissue.by_rule[directorId]
      const issues = []
      Object.keys(rule.by_issue_type ?? {}).forEach(issue => {
        if (issuesToConsider.includes(issue) && rule.by_issue_type[issue] > 0) {
          issues.push({ issue, count: rule.by_issue_type[issue] })
        }
      })
      dataToUpdateByRule.push({
        director: directorId,
        issues,
        recordsWithIssues: rule.records_with_issues,
        totalIssues: rule.total_issues,
      })
    })

    // by_symptom → dataToUpdateBySymptom
    const dataToUpdateBySymptom = []
    Object.keys(recordissue.by_symptom ?? {}).forEach(issueType => {
      const symptom = recordissue.by_symptom[issueType]
      const objects = []
      Object.keys(symptom.by_entity ?? {}).forEach(entity => {
        if (symptom.by_entity[entity] > 0) {
          objects.push({ object: entity, count: symptom.by_entity[entity] })
        }
      })
      dataToUpdateBySymptom.push({
        issue: issueType,
        objects,
        totalIssues: symptom.total_issues,
      })
    })

    // grid_totals
    const gridTotals = {
      uniqueRecords: recordissue.grid_totals?.unique_records ?? 0,
      totalIssues: recordissue.grid_totals?.total_issues ?? 0,
    }

    // written → fixedIssues, fixErrors, undoFixErrors, queuedDataIssues (director-keyed)
    const queuedDataIssues = []
    Object.keys(recordissue.written ?? {}).forEach(directorId => {
      const w = recordissue.written[directorId]
      const fixActions = w.fix_actions ?? {}
      const fixedManual = fixActions.manual || 0
      const fixedAuto = fixActions.auto || 0
      const fixedUndone = w.undone || 0
      if (fixedManual + fixedAuto + fixedUndone > 0) {
        fixedIssues.push({
          director: directorId,
          countManual: fixedManual,
          countAuto: fixedAuto,
          countUndone: fixedUndone,
        })
      }
      if (w.failed > 0) {
        fixErrors.push({ director: directorId, count: w.failed })
      }
      if (w.undo_failed > 0) {
        undoFixErrors.push({ director: directorId, count: w.undo_failed })
      }

      const q = w.queued ?? {}
      const countManual = q.manual || 0
      const countAuto = q.auto || 0
      const countUndo = q.undo || 0
      if (countManual + countAuto + countUndo > 0) {
        queuedDataIssues.push({
          director: directorId,
          countManual,
          countAuto,
          countUndo,
        })
      }
    })

    return {
      overviewStats,
      duplicateSetsToReview,
      mergedDuplicatesSets,
      queuedDuplicateSets,
      undoMergeErrors,
      mergeErrors,
      dataToUpdateByRule,
      dataToUpdateBySymptom,
      gridTotals,
      totalRecords: response.quality?.total_records ?? null,
      fixedIssues,
      fixErrors,
      undoFixErrors,
      queuedDataIssues,
      lastGenerateTime: response.last_generate_time,
    }
  })
}
